import { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import { BasketItem } from '../../../../types/basket';
import { useCheckoutState } from '../../../../utils/checkoutState';
import { reportErrorToSentry } from '../../../../utils/initializeSentry';
import { showDefaultError } from '../../Notifications';
import './style.scss';

type QuantityProps = {
  item: BasketItem;
  maxDigits?: number;
};

const Quantity = ({ item, maxDigits = 2 }: QuantityProps) => {
  const { setBasketItemQuantity, setBasketEmpty } = useCheckoutState();
  const [value, setValue] = useState(item.quantity.toString());
  const hasFocus = useRef<boolean>(false);

  useEffect(() => {
    if (!hasFocus.current) {
      setValue(item.quantity.toString());
    }
  }, [item]);

  const updateQuantity = async () => {
    const numericValue = parseInt(value);
    if (!value || numericValue < 0) {
      setValue(item.quantity.toString());
      return;
    }
    if (numericValue === item.quantity) {
      return;
    }
    try {
      await setBasketItemQuantity(
        { id: item.id, quantity: numericValue },
        numericValue > item.quantity ? 'add_to_cart' : 'remove_from_cart'
      );
    } catch (e: any) {
      setValue(item.quantity.toString());
      if (e.statusCode === 204) {
        setBasketEmpty(true);
        return;
      }
      console.error(e);
      showDefaultError();
      reportErrorToSentry(e);
    }
  };

  const onBlur = () => {
    hasFocus.current = false;
    updateQuantity();
  };

  const onFocus = () => (hasFocus.current = true);

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  return (
    <input
      type="number"
      inputMode="numeric"
      maxLength={maxDigits}
      className="quantity"
      disabled={item.isQuantityChangeDisabled}
      value={value}
      onChange={({ currentTarget }) => {
        if (currentTarget.value.length > maxDigits) {
          return;
        }
        setValue(currentTarget.value);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
};

export default Quantity;
